/**
 * Which consent privacy sections a list of study countries gives, as one
 * comparable string. A MIRROR of the server's classification:
 *
 *   - COUNTRY_NAMES  = functions/src/studyProfile/countryNames.ts
 *   - packCodeForCountry = packForCountry in studyProfile/jurisdictionPacks.ts
 *   - privacyPlanKey = privacyPlanKey(consentPrivacyPlan(...)) in
 *                      functions/src/consent/consentJurisdiction.ts
 *
 * The page cannot import server code at build time, so the small tables are
 * copied here; consentSectionOrder.test.ts checks every two-letter code, the
 * names table, and the key for a spread of country lists against the server.
 * Change both together.
 *
 * Pure, no React, so it is tested from functions' vitest (vitest.web.config.ts).
 */

/** Keys: uppercased, dots, spaces and hyphens removed. Covered countries only. */
export const COUNTRY_NAMES: Record<string, string> = {
  USA: 'US',
  UNITEDSTATES: 'US',
  UNITEDSTATESOFAMERICA: 'US',
  PUERTORICO: 'PR',
  GUAM: 'GU',
  USVIRGINISLANDS: 'VI',
  UNITEDSTATESVIRGINISLANDS: 'VI',
  AMERICANSAMOA: 'AS',
  NORTHERNMARIANAISLANDS: 'MP',
  USMINOROUTLYINGISLANDS: 'UM',
  UNITEDSTATESMINOROUTLYINGISLANDS: 'UM',
  UNITEDKINGDOM: 'GB',
  GREATBRITAIN: 'GB',
  ENGLAND: 'GB',
  SCOTLAND: 'GB',
  WALES: 'GB',
  NORTHERNIRELAND: 'GB',
  AUSTRALIA: 'AU',
  CANADA: 'CA',
  AUSTRIA: 'AT',
  BELGIUM: 'BE',
  BULGARIA: 'BG',
  CROATIA: 'HR',
  CYPRUS: 'CY',
  CZECHREPUBLIC: 'CZ',
  CZECHIA: 'CZ',
  DENMARK: 'DK',
  ESTONIA: 'EE',
  FINLAND: 'FI',
  FRANCE: 'FR',
  GERMANY: 'DE',
  GREECE: 'GR',
  HUNGARY: 'HU',
  IRELAND: 'IE',
  ITALY: 'IT',
  LATVIA: 'LV',
  LITHUANIA: 'LT',
  LUXEMBOURG: 'LU',
  MALTA: 'MT',
  NETHERLANDS: 'NL',
  THENETHERLANDS: 'NL',
  POLAND: 'PL',
  PORTUGAL: 'PT',
  ROMANIA: 'RO',
  SLOVAKIA: 'SK',
  SLOVAKREPUBLIC: 'SK',
  SLOVENIA: 'SI',
  SPAIN: 'ES',
  SWEDEN: 'SE',
  ICELAND: 'IS',
  LIECHTENSTEIN: 'LI',
  NORWAY: 'NO',
};

/** EU member states plus the EEA states that apply the Clinical Trials Regulation. */
const EU_EEA = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'EL', 'HU',
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  'IS', 'LI', 'NO',
]);

/** The US territories, under the US pack (US_TERRITORIES in jurisdictionPacks.ts). */
const US_TERRITORIES = new Set(['PR', 'GU', 'VI', 'AS', 'MP', 'UM']);

/** The pack a country code falls under, or null when no pack covers it. */
export function packCodeForCountry(code: string): string | null {
  const c = String(code || '').trim().toUpperCase();
  if (c === 'EU' || EU_EEA.has(c)) return 'EU';
  if (c === 'UK' || c === 'GB') return 'GB';
  if (c === 'AU' || c === 'US' || c === 'CA') return c;
  if (US_TERRITORIES.has(c)) return 'US';
  return null;
}

/** A Study Settings entry as the code the server uses (buildStudyProfile). */
function normaliseCountry(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const upper = raw.trim().toUpperCase();
  if (!upper) return '';
  const key = upper.replace(/[.\s-]+/g, '');
  if (COUNTRY_NAMES[key]) return COUNTRY_NAMES[key];
  if (/^[A-Z]{2}$/.test(key)) return key;
  return upper;
}

const NON_US_ORDER = ['EU', 'GB', 'CA', 'AU'];

/** Equal for two country lists exactly when they give the same consent sections. */
export function privacyPlanKey(countries: unknown): string {
  const codes = [...new Set((Array.isArray(countries) ? countries : []).map(normaliseCountry).filter(Boolean))];
  if (codes.length === 0) return 'tbd';
  const packs = new Set(codes.map(packCodeForCountry).filter((p): p is string => p !== null));
  const uncovered = [...new Set(codes.filter((c) => packCodeForCountry(c) === null))].sort();
  return `${packs.has('US') ? 'US' : '-'}|${NON_US_ORDER.filter((p) => packs.has(p)).join(',')}|${uncovered.join(',')}`;
}
